<template>
  <div class="modal-card modal-card-large">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <div class="header-content">
        <h1 class="title">Manage Users</h1>
      </div>
    </div>
    <div class="modal-card-body">
      <div v-if="isLoading" class="loading-container">
        <div class="spinner"></div>
        <p class="loading-text">Loading users...</p>
      </div>
      <div v-else-if="users.length === 0" class="empty-state">
        <p>No users found</p>
      </div>
      <div v-else class="user-list">
        <div v-for="user in users" :key="user.userId" class="user-card">
          <div class="user-info">
            <IconCircle :initials="getUserInitials(user)" bg-color="var(--icon-user-bg)" color="var(--icon-user-color)"
              :size="32" />
            <div class="user-details">
              <div class="user-name-row">
                <span class="user-name">{{ user.name }}</span>
                <span v-if="user.isAdmin" class="admin-badge">Admin</span>
              </div>
              <span class="user-email">{{ user.email }}</span>
            </div>
          </div>
          <div v-if="currentUser && currentUser.isAdmin" class="user-actions">
            <button class="button icon-button" @click="handleEditUser(user)" title="Edit user">
              <PencilIcon :size="18" />
            </button>
            <button class="button icon-button danger-icon" @click="handleDeleteUser(user)" title="Delete user">
              <DeleteIcon :size="18" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="currentUser && currentUser.isAdmin" class="modal-card-footer">
      <button class="button primary form-button" @click="handleCreateUser">
        Create User
      </button>
    </div>
  </div>
</template>

<script>
import IconCircle from '@/components/common/IconCircle.vue';
import CloseIcon from 'vue-material-design-icons/Close.vue';
import PencilIcon from 'vue-material-design-icons/Pencil.vue';
import DeleteIcon from 'vue-material-design-icons/Delete.vue';
import * as api from '@/api';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';

export default {
  name: "UserListModal",
  components: {
    IconCircle,
    CloseIcon,
    PencilIcon,
    DeleteIcon
  },
  emits: ['edit-user', 'delete-user', 'create-user', 'close'],
  data() {
    return {
      users: [],
      isLoading: false,
      currentUser: null
    }
  },
  created() {
    this.currentUser = queryClient.getQueryData(['user']) || null;
    this.loadUsers();
  },
  methods: {
    async loadUsers() {
      try {
        this.isLoading = true;
        const response = await queryClient.fetchQuery({
          queryKey: ['users'],
          queryFn: () => api.fetchUsers()
        });
        this.users = response.users || [];
      } catch (err) {
        console.error('Failed to fetch users:', err);
        alertService.error('Failed to load users: ' + err.message);
        this.users = [];
      } finally {
        this.isLoading = false;
      }
    },
    getUserInitials(user) {
      return user.name ? user.name.charAt(0).toUpperCase() : 'U';
    },
    handleCreateUser() {
      this.$emit('create-user');
    },
    handleEditUser(user) {
      this.$emit('edit-user', user);
    },
    handleDeleteUser(user) {
      this.$emit('delete-user', user);
    }
  }
}
</script>

<style scoped>
.modal-card-large {
  max-width: 500px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #8c8c8c;
  font-size: 14px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #8c8c8c;
  font-size: 14px;
}

.user-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.user-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.user-card:last-child {
  border-bottom: none;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.user-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #262626;
}

.user-email {
  font-size: 13px;
  color: #8c8c8c;
}

.admin-badge {
  display: inline-block;
  padding: 2px 8px;
  background-color: #e6f4ff;
  color: var(--primary-color);
  font-size: 11px;
  font-weight: 600;
  border-radius: var(--border-radius);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.user-actions {
  display: flex;
  gap: 4px;
}

.icon-button {
  padding: 6px;
  min-width: auto;
  height: 31px;
  width: 31px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius);
}

.icon-button.danger-icon {
  color: #cf1322;
}

.icon-button.danger-icon:hover {
  background-color: #fff1f0;
  border-color: #ffccc7;
}
</style>
